import { researchTierLabel } from '../research-presentation';
/* eslint-disable @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-non-null-assertion -- defensive reads of projection fields that may be absent on older bridges */
import { useMemo, useState } from 'preact/hooks';
import type { UiResearchNode } from '../bridge';

/** Research tree graph (P612): branch pills across the top, then tier rows
 *  on a connector rail, one node card per row (phone-first). Pure display:
 *  the parent panel owns expansion, start dispatch and cash checks. */

const BRANCH_LABEL: Record<string, string> = {
  veterinary: 'Veterinary',
  tracking: 'Tracking tech',
  genetics: 'Genetics',
  ecology: 'Ecology',
  drones: 'Drones & GPS',
  other: 'Other'
};

export function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ResearchGraph(props: {
  tree: UiResearchNode[];
  activeStudyId?: string | null;
  openId: string | null;
  onToggle(id: string): void;
  renderBadge(node: UiResearchNode): preact.ComponentChildren;
  renderDetail(node: UiResearchNode): preact.ComponentChildren;
}) {
  const { tree, activeStudyId, openId, onToggle, renderBadge, renderDetail } = props;
  const branches = useMemo(() => {
    const seen = new Set<string>();
    for (const n of tree) seen.add(n.branch ?? 'other');
    return [...seen].sort();
  }, [tree]);
  const [branch, setBranch] = useState<string | null>(null);
  // Default view: the running study's branch, else the first branch with a
  // startable node, else the first branch. Alphabetically first can be all
  // locked (drones) - a dead first impression.
  const defaultBranch = useMemo(() => {
    if (activeStudyId) {
      const a = tree.find((n) => n.id === activeStudyId);
      if (a) return a.branch ?? 'other';
    }
    const avail = tree.find((n) => n.state === 'available');
    if (avail) return avail.branch ?? 'other';
    return branches[0] ?? 'other';
  }, [tree, activeStudyId, branches]);
  const activeBranch = branch ?? defaultBranch;
  const tiers = useMemo(() => {
    const m = new Map<number, UiResearchNode[]>();
    for (const n of tree) {
      if ((n.branch ?? 'other') !== activeBranch) continue;
      const t = n.tier ?? 0;
      if (!m.has(t)) m.set(t, []);
      m.get(t)!.push(n);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [tree, activeBranch]);

  return (
    <div>
      {branches.length > 1 && (
        <div role="tablist" aria-label="Research branches" style="display:flex;gap:6px;overflow-x:auto;margin-top:12px;padding-bottom:4px">
          {branches.map((b) => (
            <button
              key={b}
              role="tab"
              aria-selected={b === activeBranch}
              class={b === activeBranch ? 'btn' : 'btn btn-quiet'}
              style="white-space:nowrap;padding:6px 12px;font-size:12px;min-height:0"
              onClick={() => { setBranch(b); }}
            >
              {BRANCH_LABEL[b] ?? humanize(b)}
            </button>
          ))}
        </div>
      )}
      {tiers.map(([tier, list]) => (
        <div key={tier} style="margin-top:10px;padding-left:10px;border-left:2px solid var(--ui-border)">
          <div class="row-label" style="margin:2px 0 6px">{researchTierLabel(tier)}</div>
          {list.map((n) => {
            const open = openId === n.id;
            return (
              <div key={n.id} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:10px 12px">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => { onToggle(n.id); }}
                  style="all:unset;display:block;width:100%;cursor:pointer"
                >
                  <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
                    <span class="row-value" style="font-size:14px">{n.name}</span>
                    {renderBadge(n)}
                  </div>
                  <div class="row-label" style="margin-top:2px">
                    {n.state === 'active' || n.state === 'completed' ? 'Base cost ' : ''}${(n.costCents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })} - {n.durationDays}d
                    {n.state === 'locked' && n.missingPrereqs.length > 0 ? ` - needs ${n.missingPrereqs.join(', ')}` : ''}
                  </div>
                </button>
                {open && renderDetail(n)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
